import {faPlus} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {filter, find, findIndex, map} from 'lodash';
import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import Button from 'components/Button';
import KyError from 'components/Errors/KyError';
import Loading from 'components/Loading';
import FullFluidWidthTemplate from 'components/Templates/FullFluidWidth';
import useModalService from 'components/shared/ModalService';
import useAppContext from 'conf/AppContext';
import {useGet} from 'helpers/KyHooks';
import CompanyForm from 'pages/admin/company/Form';
import CompanyList from 'pages/admin/company/List';



export default function CompanyPage() {
  const appContext = useAppContext();
  const modalService = useModalService();
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  
  const {response: techStackOptions, error: techStackOptionsErr} = useGet('api/v1/techStacks/list', {
    cacheResponse: false,
  }, {
    transform: (r) => map(r?.data?.techStacks, (val) => ({
      label: val?.techStackName,
      value: val?.techStackName,
      category: val?.category,
    })),
  });
    
  useEffect(() => {
    const abortController = new AbortController();
    const loadData = async () => {
      try {
        const rez = await appContext.api.get('api/v1/companies/list', {
          parseJson: true,
        }, abortController);
        setData(rez?.data.companies);
      } catch (err) {
        setError(err);
      }
    };
    // noinspection JSIgnoredPromiseFromCall
    loadData();

    return () => {
      abortController.abort();
    };

  }, []);
  

  const closeHandler = () => {
    modalService.close();
  };
  
  const saveHandler = (val) => {
    const companyId = val?.data?.company?.companyId;
    if (companyId) {
      const existingIdx = findIndex(data, (ts) => ts.companyId === companyId);
      if (existingIdx >= 0) {
        const newCompany = [...data];
        newCompany.splice(existingIdx, 1, val?.data?.company);
        setData(newCompany);
      } else {
        setData([val?.data?.company, ...data]);
      }
      toast.success(val?.message);
    }
    modalService.close();
  };
  
  const openNewEditor = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    
    let company;
    const closestAncestor = evt.target.closest('button[data-id]');
    if (closestAncestor && closestAncestor.getAttribute('data-id')) {
      company = closestAncestor.getAttribute('data-id');
      if (!company) {
        appContext.toastError(`Cannot find company ${company}! Try reloading the page.`);
        return;
      }
    } else {
      appContext.toastError('Cannot find data-id! Try reloading the page.');
      return;
    }
    
    if (error) {
      setError(false);
    }
    
    modalService.open({
      closeHandler,
      style: {
        width: '40em',
        overflow: 'unset',
      },
      content: (
        <CompanyForm
          techStackOptions={techStackOptions}
          onSave={saveHandler}
          onClose={closeHandler}
        />
      ),
    },
    );
  };

  const deleteSuccessHandler = (id) => {
    toast.success('Company deleted.');
    setData(filter(data, (val) => val.companyId !== id));
  };

  const deleteFailureHandler = (id, err) => {
    appContext.toastError(<KyError kyError={err} />);
  };


  const openEditEditor = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    let id;
    let company;
    const closestAncestor = evt.target.closest('button[data-id]');
    if (closestAncestor && closestAncestor.getAttribute('data-id')) {
      id = closestAncestor.getAttribute('data-id');
      if (id) {
        company = find(data, (ts) => ts.companyId === id);
        if (!company) {
          appContext.toastError(`Cannot find company ID ${id}!  Try reloading the page.`);
          return;
        }
      }
    }

    if (error) {
      setError(false);
    }

    modalService.open({
      closeHandler,
      style: {
        width: '40em',
      },
      content: (
        <CompanyForm
          techStackOptions={techStackOptions}
          company={company}
          onSave={saveHandler}
          onClose={closeHandler}
        />
      ),
    });
  };
  
  const renderCompanyBtn = () => (
    <div className="mb-2">
      <Button actionHandler={openNewEditor} data-id="company">
        <FontAwesomeIcon icon={faPlus} />Add company
      </Button>
    </div>
  );

  const allDataLoaded = techStackOptions && data;
  let body = <Loading />;
  if (error || techStackOptionsErr) {
    body = (
      <>
        <KyError kyError={error} />
        <KyError kyError={techStackOptionsErr} />
      </>
    );
  } else if (allDataLoaded) {
    if (data?.length === 0) {
      body = <p>No companies found.</p>;
    } else {
      body = (
        <CompanyList
          companies={data}
          techStackOptions={techStackOptions}
          onEdit={openEditEditor}
          onSuccess={deleteSuccessHandler}
          onFailure={deleteFailureHandler}
        />
      );
    }
  }
  
  return (
    <FullFluidWidthTemplate title="Company Page">
      {renderCompanyBtn()}
      {body}
    </FullFluidWidthTemplate>
  );
}
